import { api } from './api';
import { IExpenseReport } from '../pages/Dashboard/vehicles_management/Expenses';

// Tipos de resposta da API
interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    take: number;
    totalPages: number;
  };
}

interface ConsolidateRequest {
  director: string;
  startDate: string;
  endDate: string;
  reportIds: string[];
  // Obrigatório exceto para diretoria (sector_id = SPECIAL_SECTOR_ID)
  manager_id?: string;
}

interface SignatureRequest {
  name: string;
  style: 'cursive-1' | 'cursive-2' | 'cursive-3' | 'elegant-1' | 'elegant-2';
  cpf?: string;
  certificate?: string;
}

export interface IExpenseEmailLog {
  id: string;
  recipient_user_id?: string | null;
  recipient_email: string;
  type:
    | 'participant_created'
    | 'participant_updated'
    | 'reimbursement'
    | 'signature'
    | 'participant_signature'
    | 'manager_signature'
    | 'accounting';
  subject: string;
  status: 'sent' | 'failed';
  error?: string | null;
  signing_order?: number | null;
  link?: string | null;
  sent_at: string;
}

export interface IExpenseRevertLog {
  id: string;
  reason?: string | null;
  reverted_at: string;
  reverted_by?: string | null;
  reverted_by_name?: string | null;
  previous_status?: string | null;
  previous_signature_status?: string | null;
  previous_manager_signature_status?: string | null;
}

class ExpenseReportsService {
  private readonly baseUrl = '/expense-reports';

  /**
   * Mapeia dados snake_case do backend para camelCase do frontend
   */
  // eslint-disable-next-line class-methods-use-this
  private mapReport(report: any): IExpenseReport {
    // Montar objeto signature a partir dos campos snake_case do backend
    // Assinatura do usuário
    const signature =
      report.signature_status === 'signed' && report.signature_name
        ? {
            name: report.signature_name,
            style: report.signature_style || 'elegant-1',
            cpf: report.signature_cpf,
            signedAt: report.signed_at,
            certificate: report.signature_certificate,
          }
        : report.signature;

    // Assinatura do gestor
    const managerSignature =
      report.manager_signature_status === 'signed' &&
      report.manager_signature_name
        ? {
            name: report.manager_signature_name,
            style: report.manager_signature_style || 'elegant-1',
            cpf: report.manager_signature_cpf,
            signedAt: report.manager_signed_at,
            certificate: report.manager_signature_certificate,
          }
        : report.managerSignature;

    return {
      ...report,
      signature,
      managerSignature,
      director: report.user_id || report.director,
      director_name: report.user?.name || report.director_name,
      signatureStatus: report.signature_status || report.signatureStatus,
      signatureRequestedAt:
        report.signature_requested_at || report.signatureRequestedAt,
      signedAt: report.signed_at || report.signedAt,
      managerSignatureStatus:
        report.manager_signature_status || report.managerSignatureStatus,
      managerSignatureRequestedAt:
        report.manager_signature_requested_at ||
        report.managerSignatureRequestedAt,
      managerSignedAt: report.manager_signed_at || report.managerSignedAt,
      // Normalizar campos do backend
      branches_id: report.branch_id || report.branches_id,
      observation: report.observation || report.observation,
      isConsolidated: report.is_consolidated || report.isConsolidated,
      consolidatedAt: report.consolidated_at || report.consolidatedAt,
      consolidatedReportId:
        report.consolidated_report_id || report.consolidatedReportId,
      participants: report.participants
        ? report.participants.map((p: any) => ({
            ...p,
            name: p.name || p.user?.name,
            payment_required:
              p.payment_required != null ? !!p.payment_required : true,
          }))
        : undefined,
      rateio: report.rateio
        ? {
            mode: report.rateio.mode,
            allocations: (report.rateio.allocations || []).map((a: any) => ({
              ...a,
              percentage:
                a.percentage != null ? Number(a.percentage) : undefined,
              value: a.value != null ? Number(a.value) : undefined,
              item_ids: a.item_ids || [],
            })),
          }
        : undefined,
      items: (report.items || []).map((item: any) => {
        const receipt = item.receipt
          ? {
              ...item.receipt,
              name: item.receipt.original_name || item.receipt.name,
              type: item.receipt.mime_type || item.receipt.type,
            }
          : undefined;

        return {
          ...item,
          amount: item.amount != null ? Number(item.amount) : undefined,
          additional_km:
            item.additional_km != null ? Number(item.additional_km) : undefined,
          receipt,
        };
      }),
    };
  }

  /**
   * Lista todos os relatórios com filtros opcionais
   */
  async list(params?: {
    page?: number;
    take?: number;
    director?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    showConsolidated?: boolean;
  }): Promise<PaginatedResponse<IExpenseReport>> {
    const response = await api.get<PaginatedResponse<any>>(this.baseUrl, {
      params: {
        page: params?.page || 1,
        take: params?.take || 100,
        ...params,
      },
    });

    return {
      ...response.data,
      data: response.data.data.map((r: any) => this.mapReport(r)),
    };
  }

  /**
   * Busca um relatório por ID
   */
  async getById(id: string): Promise<IExpenseReport> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return this.mapReport(response.data);
  }

  /**
   * Busca um relatório público (sem autenticação - para assinatura)
   */
  async getPublic(id: string): Promise<IExpenseReport> {
    const response = await fetch(
      `${api.defaults.baseURL}${this.baseUrl}/${id}/public`,
    );

    if (!response.ok) {
      throw new Error('Relatório não encontrado');
    }

    const data = await response.json();
    return this.mapReport(data);
  }

  /**
   * Cria um novo relatório de despesas.
   *
   * Fluxo em 2 etapas:
   *  1) POST /expense-reports (JSON) — cria relatório + items
   *  2) Para cada arquivo, POST /expense-reports/:id/attachments — anexa ao item
   */
  async create(
    data: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>,
    files?: { [itemIndex: number]: File },
  ): Promise<IExpenseReport> {
    // Montar items limpos (sem File no receipt)
    const cleanItems = data.items.map(item => {
      const base: any = {
        expense_type: item.expense_type,
      };
      if (item.amount != null) base.amount = item.amount;
      if (item.additional_km != null) base.additional_km = item.additional_km;
      if (item.km_start != null) base.km_start = item.km_start;
      if (item.km_end != null) base.km_end = item.km_end;
      if (item.displacement_reason)
        base.displacement_reason = item.displacement_reason;
      if (item.observations) base.observations = item.observations;
      return base;
    });

    console.log('cleanItems enviados:', JSON.stringify(cleanItems, null, 2));

    // 1) Criar o relatório como JSON
    const payload: any = {
      user_id: data.director,
      date: data.date,
      sector: data.sector,
      section: data.section,
      cost_center: data.cost_center,
      items: cleanItems,
    };

    if (data.manager_id) payload.manager_id = data.manager_id;
    if (data.branches_id) payload.branches_id = data.branches_id;
    if (data.pix_key) payload.pix_key = data.pix_key;
    if (data.bank_name) payload.bank_name = data.bank_name;
    if (data.bank_agency) payload.bank_agency = data.bank_agency;
    if (data.bank_account) payload.bank_account = data.bank_account;

    // Participantes (responsável + vinculados) — cada um com lotação,
    // dados bancários e flag de pagamento.
    if (data.participants && data.participants.length > 0) {
      payload.participants = data.participants.map(p => ({
        user_id: p.user_id,
        role: p.role,
        sector: p.sector,
        section: p.section,
        cost_center: p.cost_center,
        branch_id: p.branch_id || null,
        payment_required: p.payment_required,
        pix_key: p.pix_key || null,
        bank_name: p.bank_name || null,
        bank_agency: p.bank_agency || null,
        bank_account: p.bank_account || null,
      }));
    }

    // Rateio do custo total entre centros de custo livres.
    if (data.rateio && data.rateio.allocations.length > 0) {
      payload.rateio = {
        mode: data.rateio.mode,
        allocations: data.rateio.allocations.map(a => ({
          cost_center: a.cost_center,
          sector: a.sector || null,
          section: a.section || null,
          description: a.description || null,
          percentage: a.percentage ?? null,
          value: a.value ?? null,
          item_ids: a.item_ids || [],
        })),
      };
    }

    const response = await api.post<IExpenseReport>(this.baseUrl, payload);
    const createdReport = this.mapReport(response.data);

    // 2) Fazer upload dos anexos (se houver)
    const fileEntries = files ? Object.entries(files) : [];
    if (fileEntries.length === 0) {
      return createdReport;
    }

    // O backend retorna os items criados - precisamos casar o índice
    // enviado com o item criado pela ordem (items vêm na mesma ordem)
    const createdItems = createdReport.items || [];

    await Promise.all(
      fileEntries.map(async ([indexStr, file]) => {
        const itemIndex = Number(indexStr);
        const createdItem = createdItems[itemIndex];
        if (!createdItem) return;

        try {
          await this.uploadAttachment(createdReport.id, createdItem.id, file);
        } catch (err) {
          console.error(`Erro ao enviar anexo do item ${itemIndex}:`, err);
        }
      }),
    );

    // 3) Buscar o relatório atualizado com as URLs dos anexos
    return this.getById(createdReport.id);
  }

  /**
   * Faz upload de um anexo/comprovante para um item do relatório
   * POST /expense-reports/:id/attachments
   */
  async uploadAttachment(
    reportId: string,
    itemId: string,
    file: File,
    receiptType?: string,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('item_id', itemId);
    formData.append('receipt', file);
    if (receiptType) {
      formData.append('receipt_type', receiptType);
    }

    const response = await api.post(
      `${this.baseUrl}/${reportId}/attachments`,
      formData,
    );
    return response.data;
  }

  /**
   * Lista os anexos de um relatório
   * GET /expense-reports/:id/attachments
   */
  async listAttachments(reportId: string): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/${reportId}/attachments`);
    return response.data;
  }

  /**
   * URL de download de um recibo específico
   * GET /expense-reports/:reportId/receipts/:receiptId/download
   */
  getReceiptDownloadUrl(reportId: string, receiptId: string): string {
    return `${api.defaults.baseURL}${this.baseUrl}/${reportId}/receipts/${receiptId}/download`;
  }

  /**
   * Atualiza um relatório existente
   */
  async update(
    id: string,
    data: Partial<Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>>,
  ): Promise<IExpenseReport> {
    // Converter director -> user_id para o backend
    const { director, ...rest } = data;
    const payload =
      director !== undefined ? { ...rest, user_id: director } : rest;

    const response = await api.put<IExpenseReport>(
      `${this.baseUrl}/${id}`,
      payload,
    );
    return response.data;
  }

  /**
   * Deleta um relatório
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Envia relatório para assinatura
   */
  async sendForSignature(id: string): Promise<IExpenseReport> {
    const response = await api.patch<IExpenseReport>(
      `${this.baseUrl}/${id}/send-for-signature`,
    );
    return response.data;
  }

  /**
   * Envia email com link de assinatura via backend (provider configurado)
   * POST /expense-reports/:id/send-signature-email
   */
  async sendSignatureEmail(
    id: string,
    data: {
      url: string;
      email?: string;
      name?: string;
      subject?: string;
      message?: string;
    },
  ): Promise<void> {
    await api.post(`${this.baseUrl}/${id}/send-signature-email`, data);
  }

  /**
   * Envia email com link de assinatura para o GESTOR
   * POST /expense-reports/:id/send-manager-signature-email
   */
  async sendManagerSignatureEmail(
    id: string,
    data: {
      url: string;
      email?: string;
      name?: string;
      subject?: string;
      message?: string;
    },
  ): Promise<void> {
    await api.post(`${this.baseUrl}/${id}/send-manager-signature-email`, data);
  }

  /**
   * Envia relatório para lançamento contábil via backend
   * POST /expense-reports/:id/send-accounting-email
   */
  async sendToAccounting(
    reportId: string,
    data?: { email?: string; subject?: string; message?: string },
  ): Promise<void> {
    await api.post(
      `${this.baseUrl}/${reportId}/send-accounting-email`,
      data || {},
    );
  }

  /**
   * Notifica os participantes (responsável + vinculados) por e-mail.
   * POST /expense-reports/:id/send-participants-email
   *
   * Quando os campos são omitidos, o backend resolve os destinatários e o
   * conteúdo a partir dos participantes do relatório.
   */
  async sendParticipantsEmail(
    id: string,
    data?: { url?: string; subject?: string; message?: string },
  ): Promise<void> {
    await api.post(`${this.baseUrl}/${id}/send-participants-email`, data || {});
  }

  /**
   * Histórico de e-mails enviados de um relatório (auditoria).
   * GET /expense-reports/:id/email-logs
   */
  async getEmailLogs(id: string): Promise<IExpenseEmailLog[]> {
    const response = await api.get<
      IExpenseEmailLog[] | { data: IExpenseEmailLog[] }
    >(`${this.baseUrl}/${id}/email-logs`);
    const { data } = response;
    return Array.isArray(data) ? data : data.data || [];
  }

  /**
   * Atualiza o status do lançamento contábil
   * PUT /expense-reports/:id
   */
  async updateStatus(
    reportId: string,
    data: { status: string; observation?: string; due_date?: string },
  ): Promise<void> {
    await api.put(`${this.baseUrl}/${reportId}`, data);
  }

  /**
   * Reverte um relatório já enviado/assinado de volta ao estado editável.
   * Zera as assinaturas (responsável, gestor e envolvidos) e o status contábil.
   * Requer a permission edit_ressarcimento (gate aplicado no frontend).
   * PATCH /expense-reports/:id/revert
   */
  async revert(
    reportId: string,
    data?: { observation?: string },
  ): Promise<IExpenseReport> {
    const response = await api.patch<IExpenseReport>(
      `${this.baseUrl}/${reportId}/revert`,
      data || {},
    );
    return response.data;
  }

  /**
   * Histórico de reaberturas (reversões) de um relatório (auditoria).
   * GET /expense-reports/:id/revert-logs
   */
  async getRevertLogs(id: string): Promise<IExpenseRevertLog[]> {
    const response = await api.get<
      IExpenseRevertLog[] | { data: IExpenseRevertLog[] }
    >(`${this.baseUrl}/${id}/revert-logs`);
    const { data } = response;
    return Array.isArray(data) ? data : data.data || [];
  }

  /**
   * Assina um relatório (endpoint público)
   */
  async sign(
    id: string,
    signatureData: SignatureRequest,
  ): Promise<IExpenseReport> {
    const response = await fetch(
      `${api.defaults.baseURL}${this.baseUrl}/${id}/sign`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signatureData),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao assinar relatório');
    }

    return response.json();
  }

  /**
   * Rejeita um relatório
   */
  async reject(id: string): Promise<IExpenseReport> {
    const response = await api.post<IExpenseReport>(
      `${this.baseUrl}/${id}/reject`,
    );
    return response.data;
  }

  /**
   * Consolida múltiplos relatórios
   */
  async consolidate(data: ConsolidateRequest): Promise<IExpenseReport> {
    // Converter director -> user_id para o backend
    const { director, ...rest } = data;
    const response = await api.post<IExpenseReport>(
      `${this.baseUrl}/consolidate`,
      { ...rest, user_id: director },
    );
    return response.data;
  }

  /**
   * Gera PDF de um relatório
   */
  async generatePdf(id: string): Promise<Blob> {
    const response = await api.get(`${this.baseUrl}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Download do PDF gerado
   */
  async downloadPdf(id: string, filename?: string): Promise<void> {
    const blob = await this.generatePdf(id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `relatorio-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const expenseReportsService = new ExpenseReportsService();
